import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CheckCircle, XCircle, Edit, FileText, Clock, Loader2 } from "lucide-react";
import { ProposalAcceptanceSection } from "@/components/common/ProposalAcceptanceSection";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Gift } from "lucide-react";

interface ProposalData {
  cameras: number;
  credits: number;
  totalImages?: number;
  value: number;
  assigneeName: string;
  assigneeType: string;
  customerGenerated: boolean;
  dealType: string;
}

interface Notification {
  id: string;
  title: string;
  proposal_data: ProposalData;
  status: string;
  created_at: string;
  deal_id: string;
  access_token: string;
  is_edited: boolean;
  edited_at: string;
}

interface ProposalNotificationDetailProps {
  notification: Notification;
  userOrganizationId: string;
  proposalAcceptanceReady: Record<string, { ready: boolean; deliveryAddressId: string | null; orderReference: string | null }>;
  highlightedNotificationId: string | null;
  onAccept: (notificationId: string, dealId: string) => void;
  onReject: (notificationId: string, dealId: string, reason?: string) => void;
  onEdit: (notificationId: string, dealId: string, accessToken: string) => void;
  onProposalAcceptanceReady: (notificationId: string, ready: boolean, deliveryAddressId: string | null, orderReference: string | null, missing?: string[]) => void;
  formatDate: (dateString: string) => string;
  formatCurrency: (amount: number) => string;
}

export function ProposalNotificationDetail({
  notification,
  userOrganizationId,
  proposalAcceptanceReady,
  highlightedNotificationId,
  onAccept,
  onReject,
  onEdit,
  onProposalAcceptanceReady,
  formatDate,
  formatCurrency
}: ProposalNotificationDetailProps) {
  const [declineReason, setDeclineReason] = useState("");
  const [isAccepting, setIsAccepting] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  // Fetch full deal details so we can show the proposal breakdown + discounts
  const { data: deal } = useQuery({
    queryKey: ['proposal-notification-deal', notification.deal_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_deals')
        .select('value, total_cameras, total_images, discount_amount, deal_data')
        .eq('id', notification.deal_id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!notification.deal_id,
  });

  const dealData: any = deal?.deal_data || {};
  const isTrialPlan = dealData.plan === 'Trial';
  const displayedCommitmentYears = isTrialPlan
    ? 1
    : Number(dealData.coverage_years ?? dealData.commitment_years);
  const cameraIncludedFree = !isTrialPlan && (dealData.commitment_years === 5 || dealData.camera_price === 0);
  const discountAmt = Number(deal?.discount_amount) || 0;
  const hasAnyDiscount = cameraIncludedFree || discountAmt > 0;

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      await onAccept(notification.id, notification.deal_id);
      
      // Send order confirmation email to orders@snapshot.live
      try {
        console.log('📧 Sending order confirmation to orders@snapshot.live');
        
        // Find the order/sale associated with this deal
        const { data: saleData } = await supabase
          .from('sales')
          .select(`
            id,
            total_amount,
            order_reference,
            orders!inner (
              id,
              order_reference
            )
          `)
          .eq('deal_id', notification.deal_id)
          .single();

        if (saleData && saleData.orders && saleData.orders.length > 0) {
          const firstOrder = saleData.orders[0];
          // Send notification email to internal team without PDF
          await supabase.functions.invoke('send-order-confirmation', {
            body: {
              dealId: notification.deal_id,
              orderId: firstOrder.id,
              contactEmail: 'orders@snapshot.live', // Send to internal team only
              internalNotification: true, // Flag to skip PDF generation
              proposalAccepted: true // Flag to indicate this is from proposal acceptance
            }
          });
          
          console.log('✅ Order confirmation sent to orders@snapshot.live');
        }
      } catch (emailError) {
        console.error('⚠️ Failed to send order confirmation email:', emailError);
        // Don't fail the whole operation for email issues
      }
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDeclineConfirm = () => {
    onReject(notification.id, notification.deal_id, declineReason.trim() || undefined);
    setDeclineReason("");
  };
  return (
    <div className={`p-4 ${highlightedNotificationId === notification.id ? 'bg-primary/5 border-primary/20' : ''}`} id={`notification-${notification.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-muted p-3 rounded-md">
          <div className="text-sm text-muted-foreground">Cameras</div>
          <div className="text-lg font-semibold">{notification.proposal_data.cameras}</div>
        </div>
        <div className="bg-muted p-3 rounded-md">
          <div className="text-sm text-muted-foreground">Total Image Credits</div>
          <div className="text-lg font-semibold">
            {(
              notification.proposal_data.totalImages ??
              (notification.proposal_data.credits || 0) * (notification.proposal_data.cameras || 1)
            ).toLocaleString()}
          </div>
        </div>
        <div className="bg-muted p-3 rounded-md">
          <div className="text-sm text-muted-foreground">Initial Payment</div>
          <div className="text-lg font-semibold">
            {formatCurrency(notification.proposal_data.value)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">ex VAT</div>
        </div>
      </div>
      
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <FileText className="h-4 w-4" />
        <span>
          {notification.proposal_data.customerGenerated 
            ? 'Customer Generated'
            : `Proposal from ${notification.proposal_data.assigneeName || 'Unknown'} (${notification.proposal_data.assigneeType || 'Staff'})`
          }
        </span>
      </div>

      {hasAnyDiscount && (
        <div className="mb-4 rounded-lg border-2 border-green-400 bg-green-50 p-4">
          <div className="flex items-start gap-3">
            <Gift className="h-5 w-5 text-green-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-green-900">
                Discount applied to your proposal
              </div>
              <div className="text-xs text-green-800 mt-1 space-y-0.5">
                {cameraIncludedFree && (
                  <div>
                    • {notification.proposal_data.cameras}x camera{notification.proposal_data.cameras > 1 ? 's' : ''} included free
                    {dealData.commitment_years === 5 ? ' (5-year commitment)' : ''}
                  </div>
                )}
                {discountAmt > 0 && (
                  <div>• Additional discount: −{formatCurrency(discountAmt)}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {deal?.deal_data && (
        <div className="mb-4 p-4 rounded-md border bg-muted/30">
          <div className="text-sm font-semibold mb-2">Proposal Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {dealData.camera_model && (
              <div className="flex justify-between"><span className="text-muted-foreground">Camera model:</span><span className="font-medium">{dealData.camera_model}</span></div>
            )}
            {dealData.plan && (
              <div className="flex justify-between"><span className="text-muted-foreground">Plan:</span><span className="font-medium">{dealData.plan}</span></div>
            )}
            {dealData.images_per_day != null && !isTrialPlan && (
              <div className="flex justify-between"><span className="text-muted-foreground">Images per day:</span><span className="font-medium">{dealData.images_per_day}</span></div>
            )}
            {dealData.credits_per_camera != null && (
              <div className="flex justify-between"><span className="text-muted-foreground">Credits per camera/yr:</span><span className="font-medium">{Number(dealData.credits_per_camera).toLocaleString()}</span></div>
            )}
            {dealData.term_type === 'short' && dealData.short_term && (
              <>
                <div className="flex justify-between"><span className="text-muted-foreground">Term:</span><span className="font-medium">Short Term – {dealData.short_term.term_months} months</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Contract total images:</span><span className="font-medium">{Number(deal?.total_images || 0).toLocaleString('en-GB')}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Weekly cost:</span><span className="font-medium">£{Number(dealData.short_term.weekly || 0).toFixed(2)}</span></div>
              </>
            )}
            {dealData.term_type !== 'short' && Number.isFinite(displayedCommitmentYears) && displayedCommitmentYears > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Commitment:</span><span className="font-medium">{displayedCommitmentYears} {displayedCommitmentYears === 1 ? 'year' : 'years'}</span></div>
            )}
            {dealData.payment_structure && (
              <div className="flex justify-between"><span className="text-muted-foreground">Payment:</span><span className="font-medium">{dealData.payment_structure === 'annual' ? 'Annual' : 'Upfront'}</span></div>
            )}
            {dealData.custom_ai_model && (
              <div className="flex justify-between"><span className="text-muted-foreground">Custom AI model:</span><span className="font-medium">Yes</span></div>
            )}
            {dealData.price_per_image != null && (
              <div className="flex justify-between"><span className="text-muted-foreground">Price per image:</span><span className="font-medium">£{Number(dealData.price_per_image).toFixed(2)}</span></div>
            )}
          </div>
        </div>
      )}

      {Array.isArray(dealData.accessories) && dealData.accessories.length > 0 && (
        <div className="mb-4 p-4 rounded-md border bg-muted/30">
          <div className="text-sm font-semibold mb-2">Accessories</div>
          <div className="space-y-1 text-sm">
            {dealData.accessories.map((item: any, idx: number) => {
              const qty = Number(item.quantity) || 0;
              const unit = Number(item.unit_price) || 0;
              return (
                <div key={item.id ?? idx} className="flex justify-between">
                  <span className="text-muted-foreground">
                    {item.name} × {qty} @ £{unit.toFixed(2)}
                  </span>
                  <span className="font-medium">£{(qty * unit).toFixed(2)}</span>
                </div>
              );
            })}
            <div className="flex justify-between pt-1 border-t mt-1">
              <span className="font-semibold">Accessories Subtotal</span>
              <span className="font-semibold">
                £{dealData.accessories
                  .reduce((sum: number, it: any) => sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0)
                  .toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}

      {notification.status === 'pending' && (
        <div className="space-y-4 mt-4">
          <ProposalAcceptanceSection
            organizationId={userOrganizationId}
            onAcceptanceReady={(ready, deliveryAddressId, orderReference, missing = []) => {
              setMissingFields(missing);
              onProposalAcceptanceReady(notification.id, ready, deliveryAddressId, orderReference, missing);
            }}
            isPilot={notification.proposal_data.dealType === 'pilot' || notification.proposal_data.assigneeType === 'Trial'}
          />

          {!proposalAcceptanceReady[notification.id]?.ready && missingFields.length > 0 && (
            <div className="text-sm text-muted-foreground border rounded-md p-3 bg-muted/30">
              <div className="font-medium mb-1">Please complete before accepting:</div>
              <ul className="list-disc pl-5 space-y-0.5">
                {missingFields.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-2">
            <Button 
              onClick={handleAccept}
              disabled={!proposalAcceptanceReady[notification.id]?.ready || isAccepting}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {isAccepting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              {isAccepting ? 'Processing...' : 'Accept Proposal'}
            </Button>
            <Button 
              variant="outline"
              onClick={() => onEdit(notification.id, notification.deal_id, notification.access_token)}
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Proposal
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  Decline
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Decline Proposal</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to decline this proposal?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-2">
                  <label htmlFor="decline-reason" className="text-sm font-medium">
                    Reason for declining (optional)
                  </label>
                  <Textarea
                    id="decline-reason"
                    placeholder="Enter reason for declining this proposal..."
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    className="min-h-[80px]"
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setDeclineReason("")}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeclineConfirm}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Confirm Decline
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}

      {notification.status === 'accepted' && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle className="h-4 w-4" />
            <span className="font-medium">
              {notification.proposal_data.customerGenerated && notification.proposal_data.dealType === 'order'
                ? 'Order Confirmed'
                : 'Proposal Accepted'
              }
            </span>
          </div>
          <p className="text-green-700 text-sm mt-1">
            {notification.proposal_data.customerGenerated && notification.proposal_data.dealType === 'order'
              ? 'This customer order has been confirmed and is now being processed.'
              : 'This proposal has been moved to the Deal stage in your CRM.'
            }
          </p>
        </div>
      )}

      {notification.status === 'archived' && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
          <div className="flex items-center gap-2 text-gray-800">
            <Clock className="h-4 w-4" />
            <span className="font-medium">Proposal Archived</span>
          </div>
          <p className="text-gray-700 text-sm mt-1">
            This proposal has been archived and is no longer available for action.
          </p>
        </div>
      )}
    </div>
  );
}